import React, { useEffect, useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, TextField, MenuItem, FormControl, InputLabel,
    Select, FormControlLabel, Switch,
} from "@mui/material";

import useAuthToken from '../functions/useAuthToken';
import type { OrganisationUserDto, OrganisationUserUpsertDto, OrganisationUserRoleOptionDto } from "../models/OrganisationUsers";
import { useOrganisationsApi } from "../functions/OrganisationsApi";

const OrganisationUserDialog = ({
    open,
    organisationId,
    user,
    onClose,
    onSaved,
}: {
    open: boolean;
    organisationId: number;
    user: OrganisationUserDto | null;
    onClose: () => void;
    onSaved: () => void;
}) => {

    const getAccessToken = useAuthToken();
    const { addUser, updateUser, getRoles } = useOrganisationsApi();

    const [roles, setRoles] = useState<OrganisationUserRoleOptionDto[]>([]);
    const [displayName, setDisplayName] = useState("");
    const [email, setEmail] = useState("");
    const [userRoleId, setUserRoleId] = useState<number | "">("");
    const [isActive, setIsActive] = useState(true);

    const [isDirty, setIsDirty] = useState(false);

    // Load roles once on mount
    useEffect(() => {
        const loadRoles = async () => {
            const accessToken = await getAccessToken();
            if (!accessToken) return;
            const res = await getRoles(accessToken);
            setRoles(res);
        };
        loadRoles();
    }, []);

    // Populate fields when user/open changes
    useEffect(() => {
        setIsDirty(false);

        if (user) {
            setDisplayName(user.displayName);
            setEmail(user.email ?? "");
            setUserRoleId(user.userRoleId);
            setIsActive(user.isActive);
        } else {
            setDisplayName("");
            setEmail("");
            setUserRoleId("");
            setIsActive(true);
        }
    }, [user, open]);

    const save = async () => {
        if (!userRoleId) return;
        const accessToken = await getAccessToken();
        if (!accessToken) return;

        const dto: OrganisationUserUpsertDto = {
            userId: user?.userId ?? null,
            displayName: displayName.trim(),
            email: email.trim() || null,
            userRoleId,
            isActive,
        };

        if (user) {
            await updateUser(organisationId, user.id, dto, accessToken);
        } else {
            await addUser(organisationId, dto, accessToken);
        }

        onSaved();
    };

    const handleClose = () => {
        if (isDirty && !window.confirm("Discard unsaved changes?")) return;
        setIsDirty(false);
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
            <DialogTitle>{user ? "Edit User" : "Add User"}</DialogTitle>

            <DialogContent className="dialog-form">
                <TextField
                    label="Display Name"
                    fullWidth
                    value={displayName}
                    onChange={(e) => { setDisplayName(e.target.value); setIsDirty(true); }}
                    margin="normal"
                />

                <TextField
                    label="Email"
                    fullWidth
                    type="email"
                    value={email}
                    onChange={(e) => { setEmail(e.target.value); setIsDirty(true); }}
                    margin="normal"
                />

                <FormControl fullWidth margin="normal">
                    <InputLabel>Role</InputLabel>
                    <Select
                        label="Role"
                        value={userRoleId}
                        onChange={(e) => { setUserRoleId(Number(e.target.value)); setIsDirty(true); }}
                    >
                        {roles.map((r) => (
                            <MenuItem key={r.id} value={r.id}>
                                {r.role}
                                {r.description && (
                                    <span className="menu-item-description">{r.description}</span>
                                )}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <FormControlLabel
                    control={
                        <Switch
                            checked={isActive}
                            onChange={(e) => setIsActive(e.target.checked)}
                        />
                    }
                    label="Active"
                />
            </DialogContent>

            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                    variant="contained"
                    onClick={save}
                    disabled={!displayName.trim() || !userRoleId}
                >
                    Save
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default OrganisationUserDialog;